<script setup lang="ts">
import { $signOptions, $createSignReprints } from '@/api/signs'
import { useSnackbarStore } from '@/store/snackbar'

// types
import type {
  ReprintSideOption,
  ReprintTypeOption,
  ReprintCostTypeOption,
  ReprintArtworkTypeOption,
  ReprintPatchPriceTypeOption
} from '@/models/wayfinding/sign-reprint'

interface IProps {
  modelValue?: boolean,
  sign: any,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  sign: null,
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onSubmit'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const reprintType = ref<number>(1)
const reprintCostType = ref<number>(1)
const reprintArtworkType = ref<number>(1)
const reprintPatchPriceType = ref<number>(1)
const reprintSidesData = ref<any[]>([])
const reprintComment = ref<string>('')

const loading = ref<boolean>(false)
const submitLoading = ref<boolean>(false)

const isMounted = ref<boolean>(false)

const reprintSideOptions = ref<ReprintSideOption[]>([])
const reprintTypeOptions = ref<ReprintTypeOption[]>([])
const reprintCostTypeOptions = ref<ReprintCostTypeOption[]>([])
const reprintArtworkTypeOptions = ref<ReprintArtworkTypeOption[]>([])
const reprintPatchPriceTypeOptions = ref<ReprintPatchPriceTypeOption[]>([])

const selectedReprintSidesCount = computed(() => {
  return reprintSidesData.value.filter(i => i.is_selected)?.length || 0
})

const getSignOptions = async () => {
  loading.value = true

  try {
    const res = await $signOptions()
    reprintSideOptions.value = res.data.actions.POST.reprint_side.choices
    reprintTypeOptions.value = res.data.actions.POST.reprint_type.choices
    reprintCostTypeOptions.value = res.data.actions.POST.reprint_cost_type.choices
    reprintArtworkTypeOptions.value = res.data.actions.POST.reprint_artwork_type.choices
    reprintPatchPriceTypeOptions.value = res.data.actions.POST.reprint_patch_price_type.choices
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const prepareReprintData = () => {
  if (!props.sign?.id) return

  reprintSidesData.value = reprintSideOptions.value.filter(side => side.value <= props.sign?.type_info?.sides)
    .map(side => ({
      is_selected: true,
      reprint_side: side.value,
      reprint_side_display: side.display_name,
      reprint_patch_no: undefined,
    }))
}

const addSignReprints = async () => {
  if (!props.sign?.id) return

  submitLoading.value = true

  const payload = {
    sign_id: props.sign.id,
    reprint_sides_data: reprintSidesData.value.filter(side => side.is_selected).map(side => ({
      reprint_side: side.reprint_side,
      reprint_type: reprintType.value,
      reprint_cost_type: reprintCostType.value,
      reprint_artwork_type: reprintType.value === 1 ? reprintArtworkType.value : undefined,
      reprint_patch_price_type: reprintType.value === 2 ? reprintPatchPriceType.value : undefined,
      reprint_patch_no: reprintType.value === 2 && side.reprint_patch_no ? side.reprint_patch_no : undefined,
      reprint_patch_width: reprintType.value === 2 && side.reprint_patch_width ? side.reprint_patch_width : undefined,
      reprint_patch_height: reprintType.value === 2 && side.reprint_patch_height ? side.reprint_patch_height : undefined,
      reprint_comment: reprintComment.value
    }))
  }

  try {
    await $createSignReprints(payload)

    // clear
    reprintSidesData.value = []
    reprintComment.value = ''

    emit('onSubmit')
    emit('update:modelValue', false)

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Reprint item(s) created successfully!'
    })
  } catch (e) {
    console.log(e)
  } finally {
    submitLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  emit('onClose')
}

const clearCurrentSideData = (currentSide: any) => {
  currentSide.reprint_patch_no = undefined
  currentSide.reprint_patch_width = undefined
  currentSide.reprint_patch_height = undefined
}

onMounted(async () => {
  isMounted.value = true
})

watch(() => props.modelValue, async () => {
  // On show dialog
  if (props.modelValue) {
    await getSignOptions()

    prepareReprintData()
  }
  // on hide Dialog
  else {
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    :persistent="!!submitLoading || !!loading"
    max-width="1000"
    class="default-dialog"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!submitLoading || !!loading"
      class="v-dialog-close-btn"
      @click="closeDialog"
    />

    <VCard>
      <template #title>
        Add Sign Reprint
      </template>
      <template #subtitle>
        {{ sign?.short_name }}
      </template>
      <VCardText>
        <VContainer fluid>
          <VOverlay
            v-model="loading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VOverlay>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VBtnToggle
                v-model="reprintType"
                class="d-flex"
                color="primary"
                divided
                mandatory
                variant="outlined"
              >
                <VBtn
                  v-for="item in reprintTypeOptions"
                  :key="item.value"
                  :value="item.value"
                  class="flex-grow-1"
                >
                  {{ item.display_name }}
                </VBtn>
              </VBtnToggle>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VBtnToggle
                v-model="reprintCostType"
                class="d-flex"
                color="primary"
                divided
                mandatory
                variant="outlined"
              >
                <VBtn
                  v-for="item in reprintCostTypeOptions"
                  :key="item.value"
                  :value="item.value"
                  class="flex-grow-1"
                >
                  {{ item.display_name }}
                </VBtn>
              </VBtnToggle>
            </VCol>
          </VRow>
          <VRow>
            <VCol>
              <VTextarea
                v-model="reprintComment"
                label="Comment"
                rows="3"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol
              v-for="(side, sideKey) in reprintSidesData"
              :key="sideKey"
              cols=""
            >
              <div
                class="d-flex align-center justify-center"
                :class="{ 'disabled-checkbox-label-color': !side.is_selected }"
              >
                <VCheckbox
                  v-model="side.is_selected"
                  @update:model-value="clearCurrentSideData(side)"
                >
                  <template #label>
                    Side {{ side.reprint_side_display }}
                  </template>
                </VCheckbox>
              </div>
              <!-- If Patch Reprint -->
              <div
                v-if="reprintType === 2"
              >
                <VTextField
                  v-model="side.reprint_patch_no"
                  :disabled="!side.is_selected"
                  label="Qty of patches"
                  type="number"
                  min="1"
                  class="my-2"
                />
                <VTextField
                  v-model="side.reprint_patch_width"
                  :disabled="!side.is_selected"
                  label="Width"
                  type="number"
                  class="my-2"
                />
                <VTextField
                  v-model="side.reprint_patch_height"
                  :disabled="!side.is_selected"
                  label="Height"
                  type="number"
                  class="my-2"
                />
              </div>
              <!-- /If Patch Reprint -->
            </VCol>
          </VRow>
          <!-- If Full Reprint -->
          <VRow v-if="reprintType === 1">
            <VCol>
              <VBtnToggle
                v-model="reprintArtworkType"
                class="d-flex"
                color="primary"
                divided
                mandatory
                variant="outlined"
              >
                <VBtn
                  v-for="item in reprintArtworkTypeOptions"
                  :key="item.value"
                  :value="item.value"
                  class="flex-grow-1"
                >
                  {{ item.display_name }}
                </VBtn>
              </VBtnToggle>
            </VCol>
          </VRow>
          <!-- /If Full Reprint -->
          <!-- If Patch Reprint -->
          <VRow v-if="reprintType === 2">
            <VCol>
              <VBtnToggle
                v-model="reprintPatchPriceType"
                class="d-flex"
                color="primary"
                divided
                mandatory
                variant="outlined"
              >
                <VBtn
                  v-for="item in reprintPatchPriceTypeOptions"
                  :key="item.value"
                  :value="item.value"
                  class="flex-grow-1"
                >
                  {{ item.display_name }}
                </VBtn>
              </VBtnToggle>
            </VCol>
          </VRow>
          <!-- /If Patch Reprint -->
          <VRow>
            <VCol class="text-right">
              <VBtn
                class="py-2 px-3"
                size=""
                color="primary"
                :disabled="!selectedReprintSidesCount || !!submitLoading || !!loading"
                @click="addSignReprints"
              >
                Submit
                <VProgressCircular
                  v-if="submitLoading"
                  indeterminate
                  size="16"
                  width="2"
                  class="ml-1"
                  color="light"
                />
              </VBtn>
            </VCol>
          </VRow>
        </VContainer>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.disabled-checkbox-label-color {
  .v-checkbox .v-label {
    color: rgba(var(--v-theme-on-background), var(--v-disabled-opacity))
  }
}
</style>
